import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth, useLanguage } from '../context/AppContext';
import { Button } from '../components/ui/button';
import { 
  Camera, 
  Target, 
  ChefHat, 
  ArrowRight, 
  Dumbbell, 
  Trophy, 
  Flame, 
  Globe, 
  Facebook, 
  Play,
  Mic,
  ScanLine,
  Stethoscope,
  Heart,
  Sparkles,
  Brain,
  Scale,
  Users,
  MessageCircle,
  Zap,
  Shield,
  ShieldCheck,
  Star,
  ExternalLink,
  AlertTriangle
} from 'lucide-react';
import { motion } from 'framer-motion';
import { captureRefFromUrl } from '../utils/referral';

// Detect in-app browsers (WebViews) that block Google OAuth
const detectInAppBrowser = () => {
  const ua = navigator.userAgent || navigator.vendor || '';
  
  const rules = [
    { name: 'Facebook Messenger', test: /\bFBAN\b|\bFBAV\b|\bMessenger\b/i },
    { name: 'Instagram', test: /\bInstagram\b/i },
    { name: 'Facebook', test: /\bFB_IAB\b|\bFBIOS\b|\bFBSS\b/i },
    { name: 'Line', test: /\bLine\//i },
    { name: 'Snapchat', test: /\bSnapchat\b/i },
    { name: 'TikTok', test: /\bBytedanceWebview\b|\bTikTok\b/i },
    { name: 'LinkedIn', test: /\bLinkedIn\b/i },
    { name: 'Twitter', test: /\bTwitter\b/i },
    { name: 'WebView', test: /\bwv\b|\bWebView\b/i },
  ];
  
  for (const rule of rules) {
    if (rule.test.test(ua)) {
      return { isInApp: true, appName: rule.name };
    }
  }
  return { isInApp: false, appName: null };
};

export const Landing = () => {
  const { login } = useAuth();
  const { t, language } = useLanguage();
  const navigate = useNavigate();
  const [inAppBrowser, setInAppBrowser] = useState({ isInApp: false, appName: null });

  useEffect(() => {
    setInAppBrowser(detectInAppBrowser());
    // Silently capture ?ref=<id> if the visitor arrived via a referral link.
    // Stored in localStorage for 30 days, picked up by Presentation ribbon
    // and (TODO) the auth-email register payload once the backend supports it.
    captureRefFromUrl();
  }, []);

  // Open the current URL in the system browser (Safari/Chrome)
  const openInSystemBrowser = () => {
    const url = window.location.href;
    // Try intent-based approach for Android
    if (/android/i.test(navigator.userAgent)) {
      window.location.href = `intent://${url.replace(/^https?:\/\//, '')}#Intent;scheme=https;end`;
      return;
    }
    // For iOS in-app browsers, try to force open in Safari
    // Most in-app browsers on iOS will open window.open URLs in Safari
    window.open(url, '_system') || window.open(url, '_blank');
  };

  // Fonctionnalités uniques qui différencient l'app
  const uniqueFeatures = [
    {
      icon: Camera,
      title: language === 'fr' ? 'Photo = Macros Instantanés' : 'Photo = Instant Macros',
      desc: language === 'fr' 
        ? 'Photographie ton repas. L\'application identifie les aliments, estime les portions et calcule tes macros automatiquement. Rapide et sans casse-tête.'
        : 'Snap your meal. The app identifies foods, estimates portions and calculates your macros automatically. Fast and hassle-free.',
      color: 'from-orange-500 to-red-500'
    },
    {
      icon: Mic,
      title: language === 'fr' ? 'Coaching Vocal' : 'Voice Coaching',
      desc: language === 'fr' 
        ? 'Parle-moi de vive voix. Mon double numérique te répond avec mon approche de Chef pour ajuster ta journée, te proposer des repas santé (et même des desserts protéinés !), sans aucun jugement.'
        : 'Talk to me out loud. My digital twin responds with my Chef approach to adjust your day, suggest healthy meals (even protein desserts!), with zero judgment.',
      color: 'from-blue-500 to-purple-500'
    },
  ];

  // Fonctionnalités secondaires
  const moreFeatures = [
    {
      icon: ScanLine,
      title: language === 'fr' ? 'Scanner Code-Barres' : 'Barcode Scanner',
      desc: language === 'fr' ? 'Scanne tes produits pour les ajouter instantanément' : 'Scan products to add them instantly'
    },
    {
      icon: Scale,
      title: language === 'fr' ? 'Ajustement Portions' : 'Portion Adjustment',
      desc: language === 'fr' ? 'Modifie les grammes, les macros se recalculent automatiquement' : 'Change grams, macros recalculate automatically'
    },
    {
      icon: Trophy,
      title: language === 'fr' ? 'Badges & Défis' : 'Badges & Challenges',
      desc: language === 'fr' ? 'Gamification pour rester motivé chaque jour' : 'Gamification to stay motivated every day'
    },
    {
      icon: Heart,
      title: language === 'fr' ? 'Repas Favoris' : 'Favorite Meals',
      desc: language === 'fr' ? 'Sauvegarde et réutilise tes repas habituels en 1 clic' : 'Save and reuse your regular meals in 1 click'
    },
    {
      icon: Users,
      title: language === 'fr' ? 'Profils Famille' : 'Family Profiles',
      desc: language === 'fr' ? 'Gère plusieurs profils nutritionnels' : 'Manage multiple nutrition profiles'
    },
    {
      icon: Brain,
      title: language === 'fr' ? 'IA Proactive' : 'Proactive AI',
      desc: language === 'fr' ? 'L\'IA analyse tes données en temps réel' : 'AI analyzes your data in real-time'
    },
  ];

  // Profils cliniques
  const clinicalProfiles = [
    { icon: '🎗️', name: 'Oncologie', desc: 'Cancer / Chimio' },
    { icon: '📊', name: 'Diabète', desc: 'Type 1 & 2' },
    { icon: '🫘', name: 'Rénal', desc: 'Insuffisance' },
    { icon: '❤️', name: 'Cardio', desc: 'Hypertension' },
    { icon: '🩺', name: 'Gastro', desc: 'Crohn / Colite' },
    { icon: '🛡️', name: 'Auto-Immun', desc: 'Cœliaque' },
  ];

  return (
    <div className="min-h-screen bg-background relative overflow-hidden">
      {/* Noise overlay */}
      <div className="noise-overlay" />
      
      {/* Header */}
      <header className="relative z-10 flex items-center justify-between px-4 py-4 max-w-7xl mx-auto">
        <div className="flex items-center gap-2">
          <img
            src="/icons/yumix_logo_sidebar.png"
            alt="YUMIX"
            className="w-8 h-8 rounded-lg"
            style={{ animation: 'yumix-cockpit-pulse 3.6s ease-in-out infinite' }}
          />
          <span
            className="font-heading text-lg font-bold text-foreground hidden sm:inline"
            style={{ animation: 'yumix-text-pulse 3.6s ease-in-out infinite' }}
          >YUMIX</span>
        </div>
        <div className="flex items-center gap-3">
          <a
            href="https://www.facebook.com/groups/753188122904936/?ref=share&mibextid=NSMWBT"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 px-3 py-1.5 text-sm text-muted-foreground hover:text-[#1877F2] transition-colors"
            data-testid="header-facebook-link"
          >
            <Facebook className="w-5 h-5" />
          </a>
          <a
            href="https://www.extramiles.ca"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 px-3 py-1.5 text-sm text-muted-foreground hover:text-primary transition-colors"
            data-testid="header-website-link"
          >
            <Globe className="w-4 h-4" />
            <span className="hidden sm:inline">extramiles.ca</span>
          </a>
          <a
            href="/presentation"
            className="inline-flex items-center gap-2 px-3 py-1.5 bg-primary/20 border border-primary/40 rounded-full text-primary hover:bg-primary/30 transition-colors text-sm font-medium"
            data-testid="presentation-link"
          >
            <Play className="w-4 h-4" />
            <span>Démo</span>
          </a>
        </div>
      </header>
      
      {/* IN-APP BROWSER WARNING BANNER */}
      {inAppBrowser.isInApp && (
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4 }}
          className="relative z-20 mx-4 mt-2 mb-4"
        >
          <div className="bg-orange-500/15 border-2 border-orange-500/40 rounded-2xl p-5 max-w-xl mx-auto backdrop-blur-sm">
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 bg-orange-500/20 rounded-xl flex items-center justify-center flex-shrink-0 mt-0.5">
                <AlertTriangle className="w-5 h-5 text-orange-400" />
              </div>
              <div className="flex-1">
                <p className="font-heading text-sm font-bold uppercase text-orange-400 mb-1">
                  {language === 'fr' ? 'Navigateur non supporté' : 'Unsupported browser'}
                </p>
                <p className="text-sm text-muted-foreground leading-relaxed mb-3">
                  {language === 'fr' 
                    ? `Tu ouvres YUMIX depuis ${inAppBrowser.appName || 'une application'}. Google bloque la connexion dans ce navigateur intégré. Ouvre ce lien dans Safari ou Chrome pour te connecter.`
                    : `You're opening YUMIX from ${inAppBrowser.appName || 'an app'}. Google blocks sign-in in embedded browsers. Open this link in Safari or Chrome to sign in.`}
                </p>
                <div className="flex flex-col gap-2">
                  <Button
                    onClick={openInSystemBrowser}
                    className="w-full bg-orange-500 hover:bg-orange-600 text-white font-heading uppercase text-sm h-10 rounded-xl"
                    data-testid="open-in-browser-btn"
                  >
                    <ExternalLink className="w-4 h-4 mr-2" />
                    {language === 'fr' ? 'Ouvrir dans Safari / Chrome' : 'Open in Safari / Chrome'}
                  </Button>
                  <p className="text-xs text-muted-foreground/70 text-center">
                    {language === 'fr' 
                      ? 'Ou copie ce lien et ouvre-le dans ton navigateur'
                      : 'Or copy this link and open it in your browser'}
                  </p>
                  <button
                    onClick={() => {
                      try {
                        if (navigator.clipboard?.writeText) {
                          navigator.clipboard.writeText(window.location.href).catch(() => {});
                        }
                      } catch (e) { /* ignore clipboard errors in restricted contexts */ }
                      const el = document.getElementById('copy-confirm');
                      if (el) { el.textContent = language === 'fr' ? 'Lien copié !' : 'Link copied!'; }
                    }}
                    className="text-xs text-primary hover:underline text-center"
                    data-testid="copy-link-btn"
                  >
                    <span id="copy-confirm">{language === 'fr' ? 'Copier le lien' : 'Copy link'}</span>
                  </button>
                </div>
              </div>
            </div>
          </div>
        </motion.div>
      )}

      {/* Hero Section - REDESIGNED with EXTRA MILES heroes */}
      <div className="relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-primary/20 via-background to-pink-500/10" />
        <div className="absolute inset-0 bg-gradient-to-t from-background via-transparent to-transparent" />

        {/* Floating orange particles — décor uniquement */}
        <div className="pointer-events-none absolute inset-0 overflow-hidden" aria-hidden>
          {[...Array(8)].map((_, i) => (
            <motion.div
              key={i}
              className="absolute w-1 h-1 rounded-full bg-orange-400/40"
              style={{ left: `${10 + i * 11}%`, top: `${20 + (i % 3) * 25}%` }}
              animate={{ y: [-10, -40, -10], opacity: [0, 0.8, 0] }}
              transition={{ duration: 4 + (i % 3), repeat: Infinity, delay: i * 0.4, ease: "easeInOut" }}
            />
          ))}
        </div>

        <div className="relative max-w-7xl mx-auto px-4 py-8 md:py-12 lg:py-16">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-6 items-center">
            {/* ─── COL TEXTE ─── */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="lg:col-span-7 text-center lg:text-left order-2 lg:order-1"
            >
            {/* Badge */}
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.5, delay: 0.2 }}
              className="inline-flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-pink-500/20 to-primary/20 border border-pink-500/30 rounded-full mb-6"
            >
              <Sparkles className="w-4 h-4 text-pink-500" />
              <span className="text-sm font-medium">
                {t('landing.tagline_top')}
              </span>
              <Sparkles className="w-4 h-4 text-primary" />
            </motion.div>
            
            {/* Title */}
            <motion.h1 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.3 }}
              className="font-heading text-4xl sm:text-5xl lg:text-6xl xl:text-7xl font-black uppercase tracking-tight text-foreground mb-4"
            >
              {t('landing.headline_main')} <br />
              <span className="bg-gradient-to-r from-primary via-pink-500 to-orange-500 bg-clip-text text-transparent">
                {t('landing.headline_accent')}
              </span>
            </motion.h1>
            
            <motion.p 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.4 }}
              className="text-lg md:text-xl text-muted-foreground mb-8 max-w-2xl mx-auto lg:mx-0"
            >
              {t('landing.subline')}
            </motion.p>
            
            {/* CTA Buttons */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.5 }}
              className="flex flex-col items-center lg:items-start justify-center gap-3 mb-8"
            >
              {inAppBrowser.isInApp ? (
                <Button
                  onClick={openInSystemBrowser}
                  data-testid="get-started-btn"
                  className="bg-gradient-to-r from-primary to-pink-500 text-white hover:opacity-90 font-heading uppercase tracking-wider text-lg px-10 py-6 h-auto transition-all hover:-translate-y-1 hover:shadow-lg hover:shadow-primary/30 rounded-full"
                >
                  <span className="flex items-center gap-2">
                    {language === 'fr' ? 'Ouvrir dans le navigateur' : 'Open in browser'}
                    <ExternalLink className="w-5 h-5" />
                  </span>
                </Button>
              ) : (
                <>
                  <Button
                    onClick={() => navigate('/auth/email')}
                    data-testid="get-started-btn"
                    className="bg-gradient-to-r from-primary to-pink-500 text-white hover:opacity-90 font-heading uppercase tracking-wider text-lg px-10 py-6 h-auto transition-all hover:-translate-y-1 hover:shadow-lg hover:shadow-primary/30 rounded-full"
                  >
                    <span className="flex items-center gap-2">
                      {t('landing.cta')}
                      <ArrowRight className="w-5 h-5" />
                    </span>
                  </Button>
                  <div className="flex items-center gap-3 w-full max-w-xs">
                    <div className="flex-1 border-t border-border/40" />
                    <span className="text-xs text-muted-foreground uppercase">{t('landing.or')}</span>
                    <div className="flex-1 border-t border-border/40" />
                  </div>
                  <Button
                    variant="outline"
                    onClick={login}
                    data-testid="google-signin-btn"
                    className="gap-2 px-8 py-5 h-auto rounded-full border-border/60 hover:bg-secondary/50 text-sm"
                  >
                    <svg className="w-4 h-4" viewBox="0 0 24 24">
                      <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4"/>
                      <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
                      <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
                      <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
                    </svg>
                    {t('landing.google_login')}
                  </Button>

                  {/* Iter. 406.fr.31 — Lien explicite "Déjà membre ?
                      Connexion par email" (Yuri bug : la landing ne
                      proposait visuellement qu'un login Google qui galère
                      en preview, et l'option email était cachée derrière
                      le CTA "Get Started" peu intuitif pour un retour). */}
                  <button
                    type="button"
                    onClick={() => navigate('/auth/email')}
                    data-testid="existing-member-login"
                    className="text-xs text-muted-foreground hover:text-primary underline-offset-4 hover:underline transition-colors mt-1"
                  >
                    {language === 'fr'
                      ? "Déjà membre ? Connexion par e-mail →"
                      : 'Already a member? Sign in with email →'}
                  </button>
                </>
              )}
            </motion.div>
            
            {/* Trust badges */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.6, delay: 0.6 }}
              className="flex items-center justify-center lg:justify-start gap-6 flex-wrap"
            >
              <div className="flex items-center gap-2 text-muted-foreground text-sm">
                <Shield className="w-4 h-4 text-green-500" />
                <span>{t('landing.secure')}</span>
              </div>
              <div
                onClick={() => navigate('/security')}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') navigate('/security'); }}
                className="flex items-center gap-2 text-sm text-emerald-300/90 cursor-pointer hover:text-emerald-200 transition-colors"
                title={language === 'fr'
                  ? 'Audit sécurité OWASP Top 10 — 2026-02-20. Clique pour voir les détails.'
                  : 'OWASP Top 10 security audit — 2026-02-20. Click to see the details.'}
                data-testid="security-audited-badge"
              >
                <ShieldCheck className="w-4 h-4 text-emerald-400" />
                <span className="font-semibold underline decoration-emerald-500/40 underline-offset-4">
                  {language === 'fr' ? 'Sécurité auditée' : 'Security audited'}
                </span>
              </div>
              <div className="flex items-center gap-2 text-muted-foreground text-sm">
                <Zap className="w-4 h-4 text-yellow-500" />
                <span>{t('landing.powered_by')}</span>
              </div>
              <div className="flex items-center gap-2 text-muted-foreground text-sm">
                <Star className="w-4 h-4 text-orange-500" />
                <span>YUMIX</span>
              </div>
            </motion.div>
          </motion.div>

          {/* ─── COL HÉROS EXTRA MILES ─── */}
          <motion.div
            initial={{ opacity: 0, x: 40, scale: 0.95 }}
            animate={{ opacity: 1, x: 0, scale: 1 }}
            transition={{ duration: 0.8, delay: 0.4, ease: "easeOut" }}
            className="lg:col-span-5 order-1 lg:order-2 flex justify-center lg:justify-end items-center"
            data-testid="extra-miles-heroes"
          >
            <div className="relative w-full max-w-[280px] sm:max-w-[340px] lg:max-w-[460px]">
              {/* Pulsing radial glow behind heroes */}
              <motion.div
                aria-hidden
                className="absolute inset-0 rounded-full"
                style={{
                  background: "radial-gradient(circle at center, rgba(251,146,60,0.45) 0%, rgba(236,72,153,0.18) 40%, transparent 72%)",
                  filter: "blur(40px)",
                }}
                animate={{ scale: [1, 1.08, 1], opacity: [0.6, 0.9, 0.6] }}
                transition={{ duration: 4, repeat: Infinity, ease: "easeInOut" }}
              />

              {/* Soft outer ring — orange "energy field" */}
              <motion.div
                aria-hidden
                className="absolute -inset-3 rounded-3xl border border-orange-500/20"
                animate={{ rotate: [0, 360] }}
                transition={{ duration: 60, repeat: Infinity, ease: "linear" }}
                style={{ borderTopColor: "rgba(251,146,60,0.5)", borderRightColor: "rgba(236,72,153,0.3)" }}
              />

              {/* Heroes image with floating animation + premium drop-shadow */}
              <motion.img
                src="/extra-miles-heroes.png"
                alt={language === 'fr' ? "Coachs Extra Miles — super-héros nutrition" : "Extra Miles Coaches — nutrition superheroes"}
                className="relative z-10 w-full h-auto select-none"
                style={{
                  filter: "drop-shadow(0 20px 40px rgba(251,146,60,0.45)) drop-shadow(0 0 80px rgba(251,146,60,0.25))",
                  // Mask the bottom edge so the heroes blend into the dark background
                  WebkitMaskImage: "linear-gradient(to bottom, black 88%, transparent 100%)",
                  maskImage: "linear-gradient(to bottom, black 88%, transparent 100%)",
                }}
                animate={{ y: [0, -10, 0] }}
                transition={{ duration: 5, repeat: Infinity, ease: "easeInOut" }}
                draggable={false}
              />

              {/* Tag-line under heroes */}
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 1.2 }}
                className="relative z-10 text-center mt-3"
              >
                <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-orange-500/10 border border-orange-500/30 backdrop-blur-sm">
                  <span className="text-[10px] font-heading uppercase tracking-[0.2em] text-orange-400">
                    {language === 'fr' ? '✦ Tes coachs Extra Miles ✦' : '✦ Your Extra Miles coaches ✦'}
                  </span>
                </div>
              </motion.div>
            </div>
          </motion.div>
          </div>
        </div>
      </div>
      
      {/* UNIQUE FEATURES - The Differentiators */}
      <div className="max-w-7xl mx-auto px-4 py-16">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="text-center mb-12"
        >
          <h2 className="font-heading text-3xl md:text-4xl font-bold uppercase tracking-tight text-foreground mb-4">
            {language === 'fr' ? 'Ce Qui Nous Rend Uniques' : 'What Makes Us Unique'}
          </h2>
          <p className="text-muted-foreground max-w-xl mx-auto">
            {language === 'fr' 
              ? 'Plus qu\'un compteur de calories. C\'est mes 26 ans d\'expérience culinaire et de coaching, encodés dans une technologie de pointe.'
              : 'More than a calorie counter. It\'s my 26 years of culinary and coaching experience, encoded in cutting-edge technology.'}
          </p>
        </motion.div>
        
        <div className="grid md:grid-cols-2 gap-6 max-w-4xl mx-auto">
          {uniqueFeatures.map((feature, index) => (
            <motion.div
              key={feature.title}
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.2 + index * 0.1 }}
              className="relative bg-card border border-border p-8 rounded-2xl hover:border-primary/50 transition-all hover:-translate-y-2 group overflow-hidden"
            >
              {/* Gradient background on hover */}
              <div className={`absolute inset-0 bg-gradient-to-br ${feature.color} opacity-0 group-hover:opacity-5 transition-opacity`} />
              
              {/* Badge if present */}
              {feature.badge && (
                <div className="absolute top-4 right-4 px-2 py-1 bg-pink-500 text-white text-[10px] font-bold uppercase rounded-full">
                  {feature.badge}
                </div>
              )}
              
              <div className={`inline-flex items-center justify-center w-14 h-14 bg-gradient-to-br ${feature.color} rounded-2xl mb-6`}>
                <feature.icon className="w-7 h-7 text-white" />
              </div>
              <h3 className="font-heading text-xl font-bold uppercase tracking-tight text-foreground mb-3">
                {feature.title}
              </h3>
              <p className="text-muted-foreground leading-relaxed">
                {feature.desc}
              </p>
            </motion.div>
          ))}
        </div>
      </div>
      
      {/* CLINICAL MODE SHOWCASE - The Game Changer */}
      <div className="bg-gradient-to-br from-pink-500/10 via-background to-primary/10 py-16">
        <div className="max-w-7xl mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center mb-12"
          >
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-pink-500/20 border border-pink-500/30 rounded-full mb-4">
              <Stethoscope className="w-4 h-4 text-pink-500" />
              <span className="text-sm font-medium text-pink-400">
                {language === 'fr' ? 'Révolutionnaire' : 'Revolutionary'}
              </span>
            </div>
            <h2 className="font-heading text-3xl md:text-4xl font-bold uppercase tracking-tight text-foreground mb-4">
              {language === 'fr' ? 'Mode Clinique' : 'Clinical Mode'}
            </h2>
            <p className="text-muted-foreground max-w-2xl mx-auto text-lg">
              {language === 'fr' 
                ? 'Tu traverses une épreuve de santé ? L\'application ne guérit pas, elle exécute le plan de ton médecin. Mon système agit comme un Chef bienveillant qui adapté automatiquement les recettes, les textures et les ingrédients à tes traitements, pour ramener le plaisir de manger dans tes journées difficiles.'
                : 'Going through a health challenge? The app doesn\'t cure, it executes your doctor\'s plan. My system acts as a caring Chef that automatically adapts recipes, textures and ingredients to your treatments, bringing back the joy of eating during tough days.'}
            </p>
          </motion.div>
          
          {/* Clinical profiles grid */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4"
          >
            {clinicalProfiles.map((profile, index) => (
              <motion.div
                key={profile.name}
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.4, delay: 0.3 + index * 0.05 }}
                className="bg-card/80 backdrop-blur border border-border rounded-xl p-4 text-center hover:border-pink-500/50 transition-colors"
              >
                <span className="text-3xl mb-2 block">{profile.icon}</span>
                <p className="font-heading text-sm font-bold uppercase text-foreground">{profile.name}</p>
                <p className="text-xs text-muted-foreground">{profile.desc}</p>
              </motion.div>
            ))}
          </motion.div>
          
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.6, delay: 0.5 }}
            className="text-center text-sm text-muted-foreground mt-6"
          >
            {language === 'fr' 
              ? '+ Sélection multiple possible (ex: Diabète + Cardio)'
              : '+ Multiple selection possible (e.g., Diabetes + Cardio)'}
          </motion.p>
        </div>
      </div>
      
      {/* MORE FEATURES - Quick Grid */}
      <div className="max-w-7xl mx-auto px-4 py-16">
        <motion.h2
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="font-heading text-2xl font-bold uppercase tracking-tight text-center text-foreground mb-8"
        >
          {language === 'fr' ? 'Et Encore Plus...' : 'And Much More...'}
        </motion.h2>
        
        <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
          {moreFeatures.map((feature, index) => (
            <motion.div
              key={feature.title}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4, delay: 0.1 + index * 0.05 }}
              className="flex items-start gap-3 p-4 bg-card/50 border border-border/50 rounded-xl"
            >
              <div className="flex-shrink-0 w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center">
                <feature.icon className="w-5 h-5 text-primary" />
              </div>
              <div>
                <h3 className="font-medium text-foreground text-sm">{feature.title}</h3>
                <p className="text-xs text-muted-foreground">{feature.desc}</p>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
      
      {/* Final CTA */}
      <div className="bg-gradient-to-r from-primary/20 via-pink-500/20 to-orange-500/20 py-16">
        <div className="max-w-3xl mx-auto px-4 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <div className="flex items-center justify-center mb-6">
              <img 
                src="/mascot-yuri.png" 
                alt="Coach Yuri" 
                className="w-20 h-20 object-contain"
              />
            </div>
            <h2 className="font-heading text-3xl md:text-4xl font-bold uppercase tracking-tight text-foreground mb-4">
              {language === 'fr' ? 'Prêt À Passer À L\'Action ?' : 'Ready To Take Action?'}
            </h2>
            <p className="text-muted-foreground mb-8 text-lg">
              {language === 'fr' 
                ? 'Ta transformation commence maintenant. Pas d\'excuses, juste des solutions concrètes et du soutien au quotidien. Un pas à la fois, une bouchée à la fois. On y va ?'
                : 'Your transformation starts now. No excuses, just concrete solutions and daily support. One step at a time, one bite at a time. Let\'s go?'}
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
              <Button
                onClick={inAppBrowser.isInApp ? openInSystemBrowser : () => navigate('/auth/email')}
                data-testid="final-cta-btn"
                className="bg-gradient-to-r from-primary to-pink-500 text-white hover:opacity-90 font-heading uppercase tracking-wider text-lg px-12 py-6 h-auto transition-all hover:-translate-y-1 hover:shadow-xl hover:shadow-primary/30 rounded-full"
              >
                <span className="flex items-center gap-2">
                  {inAppBrowser.isInApp 
                    ? (language === 'fr' ? 'Ouvrir dans le navigateur' : 'Open in browser')
                    : (language === 'fr' ? 'Commencer Maintenant - All In !' : 'Start Now - All In!')}
                  {inAppBrowser.isInApp ? <ExternalLink className="w-5 h-5" /> : <ArrowRight className="w-5 h-5" />}
                </span>
              </Button>
              {!inAppBrowser.isInApp && (
                <Button
                  variant="outline"
                  onClick={login}
                  data-testid="final-google-btn"
                  className="gap-2 px-8 py-5 h-auto rounded-full border-border/60 hover:bg-secondary/50 text-sm"
                >
                  <svg className="w-4 h-4" viewBox="0 0 24 24">
                    <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4"/>
                    <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
                    <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
                    <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
                  </svg>
                  Google
                </Button>
              )}
            </div>
            <p className="text-xs text-muted-foreground mt-4">
              {language === 'fr' ? 'Problème de connexion? ' : 'Connection issues? '}
              <a href="/force-refresh" className="text-primary hover:underline">
                {language === 'fr' ? 'Cliquez ici' : 'Click here'}
              </a>
            </p>
          </motion.div>
        </div>
      </div>
      
      {/* Footer */}
      <div className="border-t border-border bg-card/50">
        <div className="max-w-7xl mx-auto px-4 py-8">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <p className="text-muted-foreground text-center md:text-left">
              Propulsé par <span className="text-primary font-bold">YUMIX</span> • Coach Yuri Octeau
            </p>
            <div className="flex items-center gap-3">
              <a
                href="https://www.facebook.com/groups/753188122904936/?ref=share&mibextid=NSMWBT"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 px-4 py-2 bg-[#1877F2]/10 hover:bg-[#1877F2]/20 border border-[#1877F2]/30 rounded-full text-[#1877F2] font-medium transition-colors"
              >
                <Facebook className="w-4 h-4" />
                <span>Groupe Facebook</span>
              </a>
              <a
                href="https://www.extramiles.ca"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 px-4 py-2 bg-primary/10 hover:bg-primary/20 border border-primary/30 rounded-full text-primary font-medium transition-colors"
              >
                <Globe className="w-4 h-4" />
                <span>extramiles.ca</span>
              </a>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Landing;
